import { Box, Flex, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

interface Metric {
  id: string;
  /** Display value — locale-agnostic numerals, so not translated. */
  value: string;
  labelId: string;
  defaultLabel: string;
}

// RTL order: 2023 sits at the right edge and 12+ (cities) at the left, matching the design.
const METRICS: Metric[] = [
  {
    id: 'since',
    value: '2023',
    labelId: 'about.stats.metric.since',
    defaultLabel: 'Where it began',
  },
  {
    id: 'tickets',
    value: '1M+',
    labelId: 'about.stats.metric.tickets',
    defaultLabel: 'Tickets sold',
  },
  {
    id: 'experiences',
    value: '615+',
    labelId: 'about.stats.metric.experiences',
    defaultLabel: 'Experiences & events',
  },
  {
    id: 'cities',
    value: '12+',
    labelId: 'about.stats.metric.cities',
    defaultLabel: 'Cities reached',
  },
];

const MetricCard = ({ value, label }: { value: string; label: ReactNode }) => (
  <VStack
    align='center'
    justify='center'
    textAlign='center'
    spacing={{ base: '4px', md: '8px' }}
    minH={{ base: '96px', md: '120px' }}
    px={{ base: '20px', md: '28px' }}
    py={{ base: '16px', md: '24px' }}
    bg='whiteAlpha.50'
    border='1px solid'
    borderColor='whiteAlpha.100'
    borderRadius={{ base: '16px', md: '20px' }}
  >
    <Text
      as='p'
      // Force LTR so the trailing "+" stays to the right of the digits (e.g. 4+, not +4).
      dir='ltr'
      m={0}
      color='white'
      fontFamily={FONT_FAMILY}
      fontWeight='800'
      fontSize={{ base: '28px', md: '40px' }}
      lineHeight='1.1'
    >
      {value}
    </Text>
    <Text
      as='p'
      m={0}
      color='whiteAlpha.700'
      fontFamily={FONT_FAMILY}
      fontWeight='400'
      fontSize={{ base: '13px', md: '15px' }}
      lineHeight='1.3'
    >
      {label}
    </Text>
  </VStack>
);

/**
 * About page Stats section (TZ-387): a single-use dark rounded card. A coral eyebrow
 * + bold white title sit on the start (right) side, a muted intro paragraph on the
 * other, and four metrics below — a row on desktop wrapping to 2x2 on mobile. RTL.
 */
export const StatsSection = () => (
  <Box as='section' dir='rtl' bg='white' aria-labelledby='about-stats-heading'>
    <Box
      w='100%'
      maxW='container.xl'
      mx='auto'
      px={{ base: '16px', md: '100px' }}
      py={{ base: '24px', md: '40px' }}
    >
      <VStack
        align='stretch'
        spacing={{ base: '32px', md: '48px' }}
        p={{ base: '24px', md: '48px' }}
        borderRadius={{ base: '24px', md: '32px' }}
        // Warm coral glow at the bottom-left of the near-black card, per the design.
        // Set via `sx` so Chakra's gradient-token parser doesn't choke on the `%` stops.
        sx={{
          background:
            'radial-gradient(120% 120% at 0% 100%, rgba(242, 86, 60, 0.28) 0%, rgba(242, 86, 60, 0) 55%), #1B1917',
        }}
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '64px', lg: '96px' }}
          // Bottom-align so the intro paragraph sits on the title's line (below the
          // eyebrow), rather than aligning to the top of the eyebrow.
          align={{ base: 'stretch', md: 'flex-end' }}
        >
          <VStack flex='1' align='start' textAlign='start' spacing={{ base: '8px', md: '12px' }}>
            <Text
              as='p'
              m={0}
              color='#F2563C' // accent-coral (About V2 eyebrow)
              fontFamily={FONT_FAMILY}
              fontWeight='400'
              fontSize={{ base: '15px', md: '18px' }}
              lineHeight='160%'
            >
              <FormattedMessage id='about.stats.eyebrow' defaultMessage='Since 2023' />
            </Text>
            <Text
              as='h2'
              id='about-stats-heading'
              m={0}
              color='white'
              fontFamily={FONT_FAMILY}
              fontWeight='900'
              fontSize={{ base: '30px', md: '48px' }}
              lineHeight='119%'
            >
              <FormattedMessage id='about.stats.title' defaultMessage='Numbers from our moments' />
            </Text>
          </VStack>

          <Box flex='1'>
            <Text
              as='p'
              m={0}
              color='whiteAlpha.700'
              fontFamily={FONT_FAMILY}
              fontWeight='400'
              fontSize={{ base: '15px', md: '16px' }}
              lineHeight='175%'
            >
              <FormattedMessage
                id='about.stats.intro'
                defaultMessage='Every number here holds part of the experiences and events that have defined Zone from our launch until today.'
              />
            </Text>
          </Box>
        </Flex>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: '12px', md: '16px' }}>
          {METRICS.map(({ id, value, labelId, defaultLabel }) => (
            <MetricCard
              key={id}
              value={value}
              label={<FormattedMessage id={labelId} defaultMessage={defaultLabel} />}
            />
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  </Box>
);
